/**
 * CI smoke-run detection.
 *
 * The authenticated smoke suite drives the real production bundle against a
 * real hosted Supabase project. That is the whole point of it — mocking the
 * client would mock away the runtime guards it exists to catch. The one thing
 * it must NOT do is leave product-analytics rows behind, so the smoke bundle
 * is built with `VITE_CI_SMOKE=true` and every `track*` call in
 * `src/lib/analytics.ts` becomes a no-op.
 *
 * A build flag alone is not a safe switch for that, because a flag that can
 * reach a deployed environment can silently disable real analytics. Three
 * conditions must hold together, and each one closes a different gap:
 *
 *  1. `VITE_CI_SMOKE === "true"` — explicit opt-in, exact string. A truthiness
 *     check would treat "false" and "0" as on.
 *  2. `VITE_SUPABASE_URL` is exactly the approved development project. This
 *     alone would still be satisfied by the `compaire-dev` Vercel deployment,
 *     which connects to that same project.
 *  3. The browser is running on a loopback host. That is what distinguishes
 *     the Playwright `vite preview` server from `dev.compaire.co`, a Vercel
 *     preview domain, or production.
 *
 * Condition 3 is the one that makes this safe rather than merely careful.
 * Under SSR and the prerender pass there is no `window` at all, so analytics
 * stay enabled there too.
 *
 * `scripts/assert-supabase-target.mjs` enforces condition 2 again at build
 * time, from `prebuild`, so a build carrying the flag against any other
 * project fails outright instead of shipping a silently instrumented bundle.
 */

/**
 * The one development project CI is allowed to target. Written out in full,
 * as a positive allowlist — nothing in this repository denies the production
 * ref, because a denylist is only as good as its list.
 */
export const APPROVED_CI_SUPABASE_URL = "https://lskoasfvekgpyxuyhgho.supabase.co";

/**
 * Hosts the local Playwright server can be reached on. `[::1]` is the form
 * `window.location.hostname` reports for an IPv6 loopback URL; the bare `::1`
 * is accepted too so a caller passing a raw host does not fall through.
 */
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

export function isLoopbackHost(hostname: string | null | undefined): boolean {
  if (typeof hostname !== "string") return false;
  return LOOPBACK_HOSTS.has(hostname.trim().toLowerCase());
}

type CiSmokeEnv = {
  VITE_CI_SMOKE?: string;
  VITE_SUPABASE_URL?: string;
};

/**
 * Pure predicate — all three conditions, no ambient state. This is the unit
 * under test; `isCiSmokeBuild()` is the thin wrapper that reads the real
 * environment and the real browser location.
 */
export function isCiSmokeRuntime(
  env: CiSmokeEnv | null | undefined,
  hostname: string | null | undefined,
): boolean {
  if (env?.VITE_CI_SMOKE !== "true") return false;
  if (env?.VITE_SUPABASE_URL?.trim() !== APPROVED_CI_SUPABASE_URL) return false;
  return isLoopbackHost(hostname);
}

/**
 * True only inside a locally served CI smoke run. Reads the two variables by
 * explicit key so Vite statically inlines them at build time.
 */
export function isCiSmokeBuild(): boolean {
  // SSR and the prerender pass: no browser host to approve, so never suppress.
  if (typeof window === "undefined") return false;

  return isCiSmokeRuntime(
    {
      VITE_CI_SMOKE: import.meta.env.VITE_CI_SMOKE,
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
    },
    window.location?.hostname,
  );
}
